import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { makeStyles, SwipeableDrawer } from '@material-ui/core';
import shadows from '../../foundations/shadows.module.scss';
import zIndex from '../../foundations/zIndex.module.scss';
import { ChevronLeft } from '../../icons';
import { BottomSheetPageIdProvider } from '../../providers/BottomSheetPageIdProvider';
import { DeviceProvider } from '../../providers/Device';
import { IsInBottomSheetProvider } from '../../providers/IsInBottomSheetProvider';
import { OverlayContext } from '../../providers/OverlayContextProvider';
import { useTheme } from '../../providers/Theme';
import { getTestIds } from '../../utils/TestIds';
import { useStateInRef } from '../../utils/useStateInRef';
import { CloseButton } from '../CloseButton/CloseButton';
import { IconButton } from '../IconButton';
import { useModalHost } from '../OverlayContext/ModalContainer';
import { useHostInBottomSheet } from '../OverlayContext/useHostInBottomSheet';
import styles from './BottomSheet.module.scss';
import { useBottomSheetContainer } from './BottomSheetContainerContext';
export const useBottomSheetStyles = makeStyles({
    paper: (param)=>{
        let { paddingBottom, minHeight } = param;
        return _object_spread_props(_object_spread({
            borderRadius: '16px 16px 0 0',
            boxShadow: shadows.sh60,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100% - 48px - var(--capacitor-safe-area-top, 0px))',
            height: 'auto'
        }, minHeight != null && {
            minHeight: "".concat(minHeight, "px")
        }), {
            paddingBottom
        });
    },
    backdrop: {
        backgroundColor: "rgba(0,0,0,0.2)"
    }
});
export const GetBottomSheetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            BottomSheetTID: {
                BackButton: 'bottom-sheet-back-button',
                CloseButton: 'bottom-sheet-close-button'
            }
        }
    });
export function BottomSheet(props) {
    const { open, onClose, onOpen = ()=>{}, children, showBackButton, onBack, onExited, paddingBottom = 16, minHeight, testId, variant = 'default', fullHeight, BackdropComponent, joinOpenSheetStack, hideSheetOnClose } = props;
    const getProps = useStateInRef(props);
    const { isTestMode } = useContext(OverlayContext);
    const modalHost = useModalHost();
    const joinsSheetStack = joinOpenSheetStack === true;
    const sheetPage = useHostInBottomSheet({
        enabled: joinsSheetStack && open,
        hideSheetOnLeave: hideSheetOnClose,
        onRemovedFromStack: ()=>{
            var _getProps_onRemovedFromStack;
            return ((_getProps_onRemovedFromStack = getProps().onRemovedFromStack) !== null && _getProps_onRemovedFromStack !== void 0 ? _getProps_onRemovedFromStack : getProps().onClose)();
        }
    });
    const isHostedInSheet = sheetPage != null;
    useEffect(()=>{
        if (!isHostedInSheet) return undefined;
        modalHost.setDetached(true);
        return ()=>modalHost.setDetached(false);
    }, [
        isHostedInSheet,
        modalHost
    ]);
    const { BottomSheetTID } = useMemo(()=>GetBottomSheetTID(testId), [
        testId
    ]);
    const { container } = useBottomSheetContainer();
    const contentRef = useRef(null);
    const savedScrollTopRef = useRef(0);
    const classes = useBottomSheetStyles({
        paddingBottom,
        minHeight
    });
    const slideProps = useMemo(()=>{
        if (!container) return {
            onExited
        };
        return {
            onEnter: ()=>{
                savedScrollTopRef.current = container.scrollTop;
                requestAnimationFrame(()=>{
                    container.scrollTop = savedScrollTopRef.current;
                });
            },
            onExited: ()=>{
                requestAnimationFrame(()=>{
                    container.scrollTop = savedScrollTopRef.current;
                });
                onExited === null || onExited === void 0 ? void 0 : onExited();
            }
        };
    }, [
        container,
        onExited
    ]);
    useEffect(()=>{
        if (!open || !container) return;
        const prev = container.style.overflow;
        container.style.overflow = 'hidden';
        return ()=>{
            container.style.overflow = prev;
        };
    }, [
        open,
        container
    ]);
    useEffect(()=>{
        const disableSwipeIfInContent = (e)=>{
            if (getProps().open && contentRef.current && e.target && contentRef.current.contains(e.target)) {
                // This tells MUI SwipeableDrawer to not handle the swipe.
                e['muiHandled'] = true;
            }
        };
        document.addEventListener('touchstart', disableSwipeIfInContent, true);
        return ()=>{
            document.removeEventListener('touchstart', disableSwipeIfInContent, true);
        };
    }, []);
    const RefForwardingBackdrop = useMemo(()=>{
        if (!BackdropComponent) return undefined;
        return /*#__PURE__*/ React.forwardRef(function RefForwardingBackdrop(param, ref) {
            let { open: backdropOpen, onClick } = param;
            return /*#__PURE__*/ _jsx("div", {
                ref: ref,
                children: /*#__PURE__*/ _jsx(BackdropComponent, {
                    open: backdropOpen,
                    onClick: onClick
                })
            });
        });
    }, [
        BackdropComponent
    ]);
    const theme = useTheme();
    const brandedStyle = useMemo(()=>{
        var _theme_textonstandardbg, _theme_textonstandardbgsecondary;
        if (variant !== 'branded') return undefined;
        if (!theme['--bg-standard']) {
            return {
                backgroundColor: 'var(--bg-standard, #f3f5ff)',
                '--bottom-sheet-swipe-indicator-color': 'var(--text-secondary)'
            };
        }
        return _object_spread({
            backgroundColor: theme['--bg-standard'],
            color: (_theme_textonstandardbg = theme['--text-on-standard-bg']) !== null && _theme_textonstandardbg !== void 0 ? _theme_textonstandardbg : theme['--text-standard'],
            '--bottom-sheet-swipe-indicator-color': (_theme_textonstandardbgsecondary = theme['--text-on-standard-bg-secondary']) !== null && _theme_textonstandardbgsecondary !== void 0 ? _theme_textonstandardbgsecondary : theme['--text-secondary']
        }, theme);
    }, [
        variant,
        theme
    ]);
    const paperProps = useMemo(()=>{
        const style = _object_spread({}, brandedStyle, fullHeight && {
            height: 'calc(100% - 48px - var(--capacitor-safe-area-top, 0px))'
        }, container && {
            position: 'absolute',
            left: 0,
            right: 0,
            width: '100%'
        });
        const className = fullHeight ? 'seagull-bottom-sheet-full-height' : undefined;
        return {
            style,
            className
        };
    }, [
        container,
        brandedStyle,
        fullHeight
    ]);
    if (joinsSheetStack) {
        if (!open || sheetPage == null) return null;
        return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx(DeviceProvider, {
            value: {
                isMobileDevice: true
            },
            children: /*#__PURE__*/ _jsx(IsInBottomSheetProvider, {
                value: true,
                children: /*#__PURE__*/ _jsx(BottomSheetPageIdProvider, {
                    value: sheetPage.pageId,
                    children: children
                })
            })
        }), sheetPage.portalTarget);
    }
    return /*#__PURE__*/ _jsxs(SwipeableDrawer, {
        disableDiscovery: true,
        disableSwipeToOpen: true,
        classes: {
            paper: classes.paper
        },
        PaperProps: paperProps,
        anchor: "bottom",
        open: open,
        onClose: onClose,
        onOpen: onOpen,
        transitionDuration: isTestMode ? 0 : undefined,
        SlideProps: slideProps,
        ModalProps: _object_spread({}, container ? {
            container,
            disableScrollLock: true,
            style: {
                position: 'absolute',
                zIndex: zIndex.bottomSheet
            },
            BackdropProps: {
                classes: {
                    root: classes.backdrop
                },
                style: {
                    position: 'absolute'
                }
            }
        } : {
            style: {
                zIndex: zIndex.bottomSheet
            },
            BackdropProps: {
                classes: {
                    root: classes.backdrop
                }
            }
        }, RefForwardingBackdrop && {
            BackdropComponent: RefForwardingBackdrop
        }),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.top,
                children: [
                    showBackButton && /*#__PURE__*/ _jsx("div", {
                        className: styles.backButton,
                        children: /*#__PURE__*/ _jsx(IconButton, {
                            // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: BottomSheet owns the inherent fixed-semantic "back" nav action; consumers cannot rename it (rule 6a).
                            bi: {
                                name: 'back'
                            },
                            noPadding: true,
                            onClick: onBack,
                            size: "small",
                            "data-testid": BottomSheetTID.BackButton(),
                            children: /*#__PURE__*/ _jsx(ChevronLeft, {})
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.swipeIndicator
                    }),
                    /*#__PURE__*/ _jsx(CloseButton, {
                        // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: BottomSheet owns the inherent fixed-semantic "close" action; consumers cannot rename it (rule 6a).
                        bi: {
                            name: 'close'
                        },
                        noPadding: true,
                        className: styles.closeButton,
                        size: "small",
                        onClick: onClose,
                        testId: BottomSheetTID.CloseButton()
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.content,
                ref: contentRef,
                "data-testid": testId,
                children: /*#__PURE__*/ _jsx(DeviceProvider, {
                    value: {
                        isMobileDevice: true
                    },
                    children: /*#__PURE__*/ _jsx(IsInBottomSheetProvider, {
                        value: true,
                        children: children
                    })
                })
            })
        ]
    });
}
